import styled, { keyframes, css } from "styled-components";

/* ─── Float keyframes ──────────────────────────────────────────────────────
   Only `translate` is animated — static rotate/scale stay independent.     */

const floatSlow = keyframes`
  0%, 100% { translate: 0 0; }
  50%       { translate: 8px -14px; }
`;

const floatMedium = keyframes`
  0%, 100% { translate: 0 0; }
  50%       { translate: -10px -10px; }
`;

const bobbing = keyframes`
  0%, 100% { translate: 0 0; }
  50%       { translate: 0 -7px; }
`;

/* ─── Layer wrapper ────────────────────────────────────────────────────────
   FIX: height: 100% fills the full scrollable parent, not just the viewport.
   Without this, absolutely-positioned children beyond the initial viewport
   height create phantom scrollable area.

   Parent page container must have:
     position: relative;
     overflow-x: clip;    ← NOT overflow: hidden (that kills vertical scroll) */

const DecorLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;

  /* Decor PNGs are pale-blue water silhouettes designed for the light page.
     On the dark-navy page they would otherwise render as bright pale-blue
     blobs. Drop the brightness so they read as subtle deep-blue ripples. */
  [data-theme="dark"] & {
    filter: brightness(0.22) saturate(1.1);
  }
`;

/* ─── Shared float animation mixin ───────────────────────────────────────── */

const floatMixin = (p) => {
  if (p.$float === "slow")
    return css`
      animation: ${floatSlow} 9s ease-in-out infinite;
      animation-delay: ${p.$delay ?? "0s"};
    `;
  if (p.$float === "medium")
    return css`
      animation: ${floatMedium} 6.5s ease-in-out infinite;
      animation-delay: ${p.$delay ?? "0s"};
    `;
  if (p.$float === "bob")
    return css`
      animation: ${bobbing} 4.5s ease-in-out infinite;
      animation-delay: ${p.$delay ?? "0s"};
    `;
  return "";
};

const hideMobileMixin = (p) =>
  p.$hideMobile &&
  css`
    @media (max-width: 768px) {
      display: none;
    }
  `;

const positionMixin = (p) => css`
  ${p.$top !== undefined && `top:    ${p.$top};`}
  ${p.$bottom !== undefined && `bottom: ${p.$bottom};`}
  ${p.$left !== undefined && `left:   ${p.$left};`}
  ${p.$right !== undefined && `right:  ${p.$right};`}
  ${p.$rotate !== undefined && `rotate: ${p.$rotate}deg;`}
  ${p.$mirrorX && `scale: -1 1;`}
  ${p.$mirrorY && `scale: 1 -1;`}
`;

/* ─── BlurGhost ────────────────────────────────────────────────────────────
   Rendered BEHIND the sharp blob. Larger, heavily blurred, lower opacity.
   Creates a soft aqua halo that follows the transparent PNG silhouette.
   margin offset compensates for the extra size so glow stays centered.     */

const BlurGhost = styled.img`
  position: absolute;
  display: block;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;

  /* 30% wider than the sharp blob */
  width: calc(${(p) => p.$width} * 1.3);
  /* Pull back so extra size stays visually centered */
  margin-top: calc(${(p) => p.$width} * -0.15);
  margin-left: calc(${(p) => p.$width} * -0.15);

  opacity: ${(p) => (p.$opacity ?? 1) * 0.4};

  /* Dropped: mix-blend-mode (forces a separate compositing layer per
     element + a blend pass against everything behind), will-change
     (forces a layer even when idle), drop-shadow (per-pixel raster).
     Kept a smaller blur for the soft halo. */
  filter: blur(14px);

  ${positionMixin}
  ${floatMixin}
  ${hideMobileMixin}

  /* Below 1024px the float animation keeps these layers moving every
     frame, which forces the Header's backdrop-filter to rerasterize on
     every scroll. Hide the halo entirely on phones/tablets — they don't
     need the parallax aura. */
  @media (max-width: 1024px) {
    display: none;
  }
`;

/* ─── DecorImg — sharp crisp blob ─────────────────────────────────────────
   drop-shadow traces the PNG silhouette (box-shadow cannot).               */

const DecorImg = styled.img`
  position: absolute;
  display: block;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;

  width: ${(p) => p.$width};
  opacity: ${(p) => p.$opacity ?? 1};

  ${(p) =>
    p.$centerX &&
    !p.$float &&
    css`
      left: 50%;
      translate: -50% 0;
    `}

  ${positionMixin}
  ${floatMixin}
  ${hideMobileMixin}

  /* Drop the float animation below 1024px. Each animated decor moves
     under a fixed Header that runs backdrop-filter — every frame the
     Header has to rerasterize the area behind it because pixels under
     it are changing. On phones this is what eats the scroll budget. */
  @media (max-width: 1024px) {
    animation: none !important;
  }
`;

/* ─── BlobWithGlow ─────────────────────────────────────────────────────────
   Renders blurred ghost first, sharp blob on top.
   Pass $blurBg={false} to skip the glow (e.g. very small bubbles where
   the blur halo would be disproportionately large).                        */

const BlobWithGlow = ({ $blurBg = true, ...props }) => (
  <>
    {$blurBg && <BlurGhost {...props} />}
    <DecorImg {...props} />
  </>
);

/* ─── Element reference ────────────────────────────────────────────────────
   element01 — wide horizontal puddle   (landscape, footer zones)
   element02 — tall blob + droplets     (side edges, mid-page)
   element03 — soft amorphous cloud     (large corner bg blobs)
   element04 — round single bubble      (floating accent)
   element05 — teardrop / heart blob    (hero accent, pagination)
   element06 — small oval pebble blob   (small accent)
   element07 — large irregular blob     (corners)
   element08 — cluster of 3 bubbles     (bubble group accents)
   ────────────────────────────────────────────────────────────────────────── */

const DecorativeElements = () => (
  <DecorLayer aria-hidden="true">
    {/* ═══ HERO ZONE  (0 → ~500px) ════════════════════════════════════════ */}

    {/* element03 — big soft cloud blob, top-left corner */}
    <BlobWithGlow
      src="/decor/element03.webp"
      alt=""
      $top="-90px"
      $left="-160px"
      $width="320px"
      $opacity={0.26}
      $rotate={-18}
      $float="slow"
    />

    {/* element07 — large irregular blob, top-right, partly off-screen */}
    <BlobWithGlow
      src="/decor/element07.webp"
      alt=""
      $top="-70px"
      $right="-180px"
      $width="460px"
      $opacity={0.24}
      $rotate={20}
      $float="slow"
      $delay="1.4s"
    />

    {/* element05 — teardrop accent, right of hero title */}
    <BlobWithGlow
      src="/decor/element05.webp"
      alt=""
      $top="130px"
      $right="9%"
      $width="120px"
      $opacity={0.58}
      $rotate={10}
      $float="slow"
      $delay="0.8s"
      $hideMobile
    />

    {/* ═══ HERO → GRID TRANSITION  (~500 → ~900px) ════════════════════════ */}

    {/* element06 — pebble blob, right edge mid-page */}
    {/* <BlobWithGlow
      src="/decor/element06.webp"
      alt=""
      $top="560px"
      $right="-190px"
      $width="520px"
      $opacity={0.4}
      $rotate={8}
      $float="slow"
      $delay="2s"
    /> */}

    {/* element02 — tall blob with droplets, left edge */}
    {/* <BlobWithGlow
      src="/decor/element02.webp"
      alt=""
      $top="500px"
      $left="-100px"
      $width="250px"
      $opacity={0.7}
      $rotate={-80}
      $float="bob"
      $delay="0.5s"
    /> */}

    {/* ═══ FOOTER ZONE ════════════════════════════════════════════════════ */}

    {/* element06 — small pebble, bottom-left corner */}
    <BlobWithGlow
      src="/decor/element06.webp"
      alt=""
      $bottom="0px"
      $left="-100px"
      $width="240px"
      $opacity={0.27}
      $rotate={-8}
      $float="slow"
      $delay="2.3s"
    />

    {/* element04 — round bubble, bottom-right corner */}
    <BlobWithGlow
      src="/decor/element04.webp"
      alt=""
      $bottom="50px"
      $right="-100px"
      $width="300px"
      $opacity={0.23}
      $rotate={12}
      $mirrorX
      $float="slow"
      $delay="0.6s"
    />

    {/* element03 — small cloud, footer right — no ghost (too small for halo) */}
    <BlobWithGlow
      $blurBg={false}
      src="/decor/element03.webp"
      alt=""
      $bottom="50px"
      $right="100px"
      $width="58px"
      $opacity={0.42}
      $float="bob"
      $delay="2s"
    />
  </DecorLayer>
);

export default DecorativeElements;
